import { useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  format,
  addMonths,
  subMonths,
  startOfToday,
  isSameMonth,
  isSameDay,
} from 'date-fns'
import {
  createMonthGrid,
  groupAvailabilityByDate,
  groupOpponentAvailabilityByDate,
  groupProposalsByDate,
  groupMatchesByDate,
  getAllItemsForDay,
} from '../../utils/calendarHelpers'
import MonthDayCell from './MonthDayCell'
import './MonthCalendarView.css'
// TODO: Uncomment when SCSS compilation issues are resolved
// import './month-styles/index.scss'

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/**
 * MonthCalendarView - Displays a month calendar grid with availability, proposals, and matches
 *
 * @param {Object} props
 * @param {Array} props.availability - Array of availability objects with start_time property
 * @param {Array} props.opponentAvailability - Array of opponent availability objects with start_time property
 * @param {Array} props.proposals - Array of proposal objects with proposed_time property
 * @param {Array} props.matches - Array of match objects with match_datetime property
 * @param {Function} props.onDayClick - Callback when day is clicked (optional, for external control)
 * @param {Date} props.selectedDate - Currently selected date (optional, for external control)
 */
function MonthCalendarView({
  availability = [],
  opponentAvailability = [],
  proposals = [],
  matches = [],
  onDayClick: externalOnDayClick,
  selectedDate: externalSelectedDate,
}) {
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [internalSelectedDate, setInternalSelectedDate] = useState(null)

  // Use external selectedDate if provided, otherwise use internal state
  const selectedDate =
    externalSelectedDate !== undefined
      ? externalSelectedDate
      : internalSelectedDate

  const monthGrid = useMemo(() => createMonthGrid(currentMonth), [currentMonth])

  // Group data by date for efficient lookup
  const availabilityByDate = useMemo(
    () => groupAvailabilityByDate(availability),
    [availability],
  )
  const opponentAvailabilityByDate = useMemo(
    () => groupOpponentAvailabilityByDate(opponentAvailability),
    [opponentAvailability],
  )
  const proposalsByDate = useMemo(
    () => groupProposalsByDate(proposals),
    [proposals],
  )
  const matchesByDate = useMemo(() => groupMatchesByDate(matches), [matches])

  // Pre-compute all day items to avoid redundant calls during render
  const dayItemsMap = useMemo(() => {
    const map = new Map()
    monthGrid.forEach((day) => {
      const items = getAllItemsForDay(
        day.date,
        availabilityByDate,
        proposalsByDate,
        matchesByDate,
        opponentAvailabilityByDate,
      )
      map.set(day.dateString, items)
    })
    return map
  }, [
    monthGrid,
    availabilityByDate,
    opponentAvailabilityByDate,
    proposalsByDate,
    matchesByDate,
  ])

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1))
  }

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1))
  }

  const handleGoToToday = () => {
    setCurrentMonth(startOfToday())
  }

  const handleDayClick = useCallback(
    (date) => {
      // Use external handler if provided, otherwise use internal state
      if (externalOnDayClick) {
        externalOnDayClick(date)
      } else {
        setInternalSelectedDate(date)
      }
    },
    [externalOnDayClick],
  )

  // Split the 42 days into 6 weeks
  const weeks = useMemo(() => {
    const result = []
    for (let i = 0; i < 6; i++) {
      result.push(monthGrid.slice(i * 7, (i + 1) * 7))
    }
    return result
  }, [monthGrid])

  const isCurrentMonthDisplayed = isSameMonth(currentMonth, startOfToday())

  return (
    <div className="fl__month-calendar-view">
      <div className="fl__month-header">
        <button
          className="fl__month-nav-button"
          onClick={handlePrevMonth}
          aria-label="Previous month"
          data-testid="prev-month-button"
        >
          ‹
        </button>
        <h2 className="fl__month-title" data-testid="month-title">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        <button
          className="fl__month-nav-button"
          onClick={handleNextMonth}
          aria-label="Next month"
          data-testid="next-month-button"
        >
          ›
        </button>
      </div>

      {!isCurrentMonthDisplayed && (
        <div className="fl__month-actions">
          <button
            className="fl__go-to-today-button"
            onClick={handleGoToToday}
            data-testid="go-to-today-button"
          >
            Go to Today
          </button>
        </div>
      )}

      {/* Legend */}
      <div className="fl__month-legend">
        <div className="fl__legend-item">
          <span className="fl__dot fl__dot--gray" aria-hidden="true"></span>
          <span className="fl__legend-label">My Availability</span>
        </div>
        <div className="fl__legend-item">
          <span className="fl__dot fl__dot--purple" aria-hidden="true"></span>
          <span className="fl__legend-label">Opponent Availability</span>
        </div>
        <div className="fl__legend-item">
          <span className="fl__dot fl__dot--blue" aria-hidden="true"></span>
          <span className="fl__legend-label">Proposals</span>
        </div>
        <div className="fl__legend-item">
          <span className="fl__dot fl__dot--green" aria-hidden="true"></span>
          <span className="fl__legend-label">Matches</span>
        </div>
      </div>

      <div className="fl__month-grid-wrapper">
        <div className="fl__month-grid-header">
          {DAY_NAMES.map((dayName) => (
            <div key={dayName} className="fl__month-grid-header-day">
              {dayName}
            </div>
          ))}
        </div>

        <div className="fl__month-grid-body">
          {weeks.map((week) => (
            <div key={week[0].dateString} className="fl__month-grid-week">
              {week.map((day) => {
                const isSelected = selectedDate
                  ? isSameDay(day.date, selectedDate)
                  : false
                const {
                  availability,
                  opponentAvailability,
                  proposals,
                  matches,
                } = dayItemsMap.get(day.dateString) || {
                  availability: [],
                  opponentAvailability: [],
                  proposals: [],
                  matches: [],
                }

                return (
                  <MonthDayCell
                    key={day.dateString}
                    day={day}
                    isCurrentMonth={day.isCurrentMonth}
                    isSelected={isSelected}
                    availability={availability}
                    opponentAvailability={opponentAvailability}
                    proposals={proposals}
                    matches={matches}
                    onDayClick={handleDayClick}
                  />
                )
              })}
            </div>
          ))}
        </div>
      </div>

      {selectedDate && (
        <div className="fl__selected-date-info">
          Selected Date: {format(selectedDate, 'MMMM d, yyyy')}
        </div>
      )}
    </div>
  )
}

MonthCalendarView.propTypes = {
  availability: PropTypes.array,
  opponentAvailability: PropTypes.array,
  proposals: PropTypes.array,
  matches: PropTypes.array,
  onDayClick: PropTypes.func,
  selectedDate: PropTypes.instanceOf(Date),
}

export default MonthCalendarView
